/**
 * OCC Company Search Service
 * 
 * Advanced feature for searching Oklahoma Corporation Commission well data
 * by company/operator name. Supports:
 * - Partial company name matching
 * - Results sorted by distance from user location
 * - Company suggestions with well counts
 * - Statewide well search by operator
 * 
 * Competitor advantage over PetroPath - we provide better UX and more data!
 */

import { toast } from 'sonner';

export interface OCCCompanyWell {
  id: string;
  api: string;
  name: string;
  operator: string;
  status: string;
  formattedStatus: 'active' | 'inactive' | 'unknown';
  type: string;
  lat: number;
  lng: number;
  distance?: number; // Distance from user in miles
}

export interface CompanySuggestion {
  name: string;
  wellCount: number;
  normalizedName: string; // For matching
}

// OCC ArcGIS REST API endpoint
const OCC_LAYER_URL = 'https://gis.occ.ok.gov/server/rest/services/Hosted/RBDMS_WELLS/FeatureServer/220/query';

// Cache for company suggestions (reduces API calls)
let companySuggestionsCache: CompanySuggestion[] = [];
let cacheTimestamp = 0;
const CACHE_DURATION_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Fetch unique operators/companies from OCC database
 * Returns list of company names with well counts
 */
export async function fetchCompanySuggestions(searchTerm: string): Promise<CompanySuggestion[]> {
  try {
    const normalizedSearch = searchTerm.toLowerCase().trim();
    
    if (normalizedSearch.length < 2) {
      return [];
    }

    // Use cached data if available and fresh
    const now = Date.now();
    if (companySuggestionsCache.length > 0 && (now - cacheTimestamp) < CACHE_DURATION_MS) {
      return companySuggestionsCache
        .filter(c => c.normalizedName.includes(normalizedSearch))
        .sort((a, b) => b.wellCount - a.wellCount)
        .slice(0, 15);
    }

    // Query OCC for distinct operators with counts
    // Use SQL-like query to get grouped data
    const params = new URLSearchParams({
      f: 'json',
      where: `operator LIKE '%${searchTerm.toUpperCase()}%'`,
      returnGeometry: 'false',
      outFields: 'operator',
      outStatistics: JSON.stringify([{
        statisticType: 'count',
        onStatisticField: 'api',
        outStatisticFieldName: 'well_count'
      }]),
      groupByFieldsForStatistics: 'operator',
      orderByFields: 'well_count DESC',
      resultRecordCount: '50'
    });

    const response = await fetch(`${OCC_LAYER_URL}?${params.toString()}`);
    
    if (!response.ok) {
      throw new Error(`OCC Server Error: ${response.statusText}`);
    }

    const data = await response.json();
    
    if (data.error) {
      console.error('OCC API Error:', data.error);
      return [];
    }

    if (!data.features || data.features.length === 0) {
      return [];
    }

    const suggestions: CompanySuggestion[] = data.features.map((f: any) => {
      const operator = f.attributes.operator || f.attributes.OPERATOR || 'Unknown';
      const count = f.attributes.well_count || f.attributes.WELL_COUNT || 0;
      
      return {
        name: operator,
        wellCount: count,
        normalizedName: operator.toLowerCase()
      };
    });

    // Cache for future use
    companySuggestionsCache = suggestions;
    cacheTimestamp = now;

    return suggestions
      .filter(c => c.normalizedName.includes(normalizedSearch))
      .sort((a, b) => b.wellCount - a.wellCount)
      .slice(0, 15);

  } catch (error: any) {
    console.error('Error fetching company suggestions:', error);
    return [];
  }
}

/**
 * Search for wells by company/operator name
 * Returns wells sorted by distance from user location
 */
export async function searchWellsByCompany(
  companyName: string,
  userLocation?: { lat: number; lng: number }
): Promise<OCCCompanyWell[]> {
  try {
    if (!companyName || companyName.trim().length < 2) {
      return [];
    }

    // Clean up the company name for SQL query
    const cleanName = companyName.trim().toUpperCase().replace(/'/g, "''");

    const params = new URLSearchParams({
      f: 'json',
      where: `UPPER(operator) LIKE '%${cleanName}%'`,
      returnGeometry: 'true',
      outFields: 'api,well_name,operator,wellstatus,welltype',
      outSR: '4326',
      resultRecordCount: '500' // Get up to 500 wells
    });

    const response = await fetch(`${OCC_LAYER_URL}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`OCC Server Error: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.error) {
      toast.error('Failed to search OCC database');
      return [];
    }

    if (!data.features || data.features.length === 0) {
      return [];
    }

    let wells: OCCCompanyWell[] = data.features.map((f: any) => {
      const attr = f.attributes;
      const wellName = attr.well_name || attr.WELL_NAME || 'Unknown Well';
      const operator = attr.operator || attr.OPERATOR || 'Unknown Operator';
      const statusRaw = (attr.wellstatus || attr.WELLSTATUS || '').toUpperCase();
      const wellType = attr.welltype || attr.WELLTYPE || 'Unknown';
      const rawApi = attr.api || attr.API || '';

      let formattedStatus: OCCCompanyWell['formattedStatus'] = 'unknown';
      if (statusRaw === 'AC' || statusRaw.includes('ACTIVE') || statusRaw.includes('PRODUCING')) {
        formattedStatus = 'active';
      } else if (statusRaw === 'PA' || statusRaw === 'DRY' || statusRaw.includes('PLUGGED')) {
        formattedStatus = 'inactive';
      }

      const lat = f.geometry?.y || 0;
      const lng = f.geometry?.x || 0;

      return {
        id: `occ-${rawApi}`,
        api: String(rawApi),
        name: wellName,
        operator: operator,
        status: statusRaw,
        formattedStatus,
        type: wellType,
        lat,
        lng,
        distance: undefined
      };
    });

    // Filter out wells without valid coordinates
    wells = wells.filter(w => w.lat !== 0 && w.lng !== 0);

    // Calculate distances if user location is available
    if (userLocation) {
      wells = wells.map(well => ({
        ...well,
        distance: calculateDistanceMiles(
          userLocation.lat,
          userLocation.lng,
          well.lat,
          well.lng
        )
      }));

      // Sort by distance (closest first)
      wells.sort((a, b) => (a.distance || 999) - (b.distance || 999));
    }

    return wells;

  } catch (error: any) {
    console.error('Error searching wells by company:', error);
    toast.error(`Search failed: ${error.message || 'Unknown error'}`);
    return [];
  }
}

/**
 * Calculate distance between two coordinates in miles
 * Uses Haversine formula
 */
function calculateDistanceMiles(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Format distance for display
 */
export function formatDistance(miles: number | undefined): string {
  if (miles === undefined) return '';
  if (miles < 0.1) return '< 0.1 mi';
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
}

/**
 * Fetch ALL wells near a location regardless of company
 * This is the "Near Me" killer feature - finds everything within radius
 * 
 * @param userLocation User's current GPS coordinates
 * @param radiusMiles Radius to search within (default 5 miles)
 * @returns Array of nearby wells sorted by distance
 */
export async function fetchNearbyWells(
  userLocation: { lat: number; lng: number },
  radiusMiles: number = 5
): Promise<OCCCompanyWell[]> {
  try {
    // Convert miles to degrees (approximate - 1 degree ≈ 69 miles)
    const radiusDegrees = radiusMiles / 69;
    
    // Bounding box for the query
    const minLat = userLocation.lat - radiusDegrees;
    const maxLat = userLocation.lat + radiusDegrees;
    const minLng = userLocation.lng - radiusDegrees;
    const maxLng = userLocation.lng + radiusDegrees;

    const params = new URLSearchParams({
      f: 'json',
      where: '1=1', // No company filter - get ALL wells
      geometry: JSON.stringify({
        xmin: minLng,
        ymin: minLat,
        xmax: maxLng,
        ymax: maxLat,
        spatialReference: { wkid: 4326 }
      }),
      geometryType: 'esriGeometryEnvelope',
      spatialRel: 'esriSpatialRelIntersects',
      returnGeometry: 'true',
      outFields: 'api,well_name,operator,wellstatus,welltype',
      outSR: '4326',
      resultRecordCount: '200' // Limit to prevent overload
    });

    const response = await fetch(`${OCC_LAYER_URL}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`OCC Server Error: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.error) {
      console.error('OCC API Error:', data.error);
      return [];
    }

    if (!data.features || data.features.length === 0) {
      return [];
    }

    let wells: OCCCompanyWell[] = data.features.map((f: any) => {
      const attr = f.attributes;
      const wellName = attr.well_name || attr.WELL_NAME || 'Unknown Well';
      const operator = attr.operator || attr.OPERATOR || 'Unknown Operator';
      const statusRaw = (attr.wellstatus || attr.WELLSTATUS || '').toUpperCase();
      const wellType = attr.welltype || attr.WELLTYPE || 'Unknown';
      const rawApi = attr.api || attr.API || '';

      let formattedStatus: OCCCompanyWell['formattedStatus'] = 'unknown';
      if (statusRaw === 'AC' || statusRaw.includes('ACTIVE') || statusRaw.includes('PRODUCING')) {
        formattedStatus = 'active';
      } else if (statusRaw === 'PA' || statusRaw === 'DRY' || statusRaw.includes('PLUGGED')) {
        formattedStatus = 'inactive';
      }

      const lat = f.geometry?.y || 0;
      const lng = f.geometry?.x || 0;

      // Calculate distance
      const distance = calculateDistanceMiles(
        userLocation.lat,
        userLocation.lng,
        lat,
        lng
      );

      return {
        id: `occ-${rawApi}`,
        api: String(rawApi),
        name: wellName,
        operator: operator,
        status: statusRaw,
        formattedStatus,
        type: wellType,
        lat,
        lng,
        distance
      };
    });

    // Filter out wells without valid coordinates and outside exact radius
    wells = wells.filter(w => 
      w.lat !== 0 && 
      w.lng !== 0 && 
      w.distance !== undefined && 
      w.distance <= radiusMiles
    );

    // Sort by distance (closest first)
    wells.sort((a, b) => (a.distance || 999) - (b.distance || 999));

    return wells;

  } catch (error: any) {
    console.error('Error fetching nearby wells:', error);
    return [];
  }
}

export const occCompanySearch = {
  fetchCompanySuggestions,
  searchWellsByCompany,
  fetchNearbyWells,
  fetchWellsInBounds,
  formatDistance
};

/**
 * Fetch ALL wells within the visible map bounds
 * Used for showing wells on the map before any search is performed
 * 
 * @param bounds Map viewport bounds (north, south, east, west)
 * @param maxResults Maximum number of results (default 500)
 * @returns Array of wells within the bounds
 */
export async function fetchWellsInBounds(
  bounds: { north: number; south: number; east: number; west: number },
  maxResults: number = 500
): Promise<OCCCompanyWell[]> {
  try {
    const params = new URLSearchParams({
      f: 'json',
      where: '1=1', // No company filter - get ALL wells
      geometry: JSON.stringify({
        xmin: bounds.west,
        ymin: bounds.south,
        xmax: bounds.east,
        ymax: bounds.north,
        spatialReference: { wkid: 4326 }
      }),
      geometryType: 'esriGeometryEnvelope',
      spatialRel: 'esriSpatialRelIntersects',
      returnGeometry: 'true',
      outFields: 'api,well_name,operator,wellstatus,welltype',
      outSR: '4326',
      resultRecordCount: String(maxResults)
    });

    const response = await fetch(`${OCC_LAYER_URL}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`OCC Server Error: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.error) {
      console.error('OCC API Error:', data.error);
      return [];
    }

    if (!data.features || data.features.length === 0) {
      return [];
    }

    const wells: OCCCompanyWell[] = data.features.map((f: any) => {
      const attr = f.attributes;
      const wellName = attr.well_name || attr.WELL_NAME || 'Unknown Well';
      const operator = attr.operator || attr.OPERATOR || 'Unknown Operator';
      const statusRaw = (attr.wellstatus || attr.WELLSTATUS || '').toUpperCase();
      const wellType = attr.welltype || attr.WELLTYPE || 'Unknown';
      const rawApi = attr.api || attr.API || '';

      let formattedStatus: OCCCompanyWell['formattedStatus'] = 'unknown';
      if (statusRaw === 'AC' || statusRaw.includes('ACTIVE') || statusRaw.includes('PRODUCING')) {
        formattedStatus = 'active';
      } else if (statusRaw === 'PA' || statusRaw === 'DRY' || statusRaw.includes('PLUGGED')) {
        formattedStatus = 'inactive';
      }

      const lat = f.geometry?.y || 0;
      const lng = f.geometry?.x || 0;

      return {
        id: `occ-${rawApi}`,
        api: String(rawApi),
        name: wellName,
        operator: operator,
        status: statusRaw,
        formattedStatus,
        type: wellType,
        lat,
        lng,
        distance: undefined
      };
    });

    // Filter out wells without valid coordinates
    return wells.filter(w => w.lat !== 0 && w.lng !== 0);

  } catch (error: any) {
    console.error('Error fetching wells in bounds:', error);
    return [];
  }
}
